import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../../config/firebase';
import api from '../../services/api';

interface InvitationInfo {
  invitationId: string;
  companyId: string;
  companyName: string;
  companyTenantId: string | null;
  inviteEmail: string;
  inviteStatus: string;
  expiresAt: string;
  isExpired: boolean;
  isUsed: boolean;
  canAccept: boolean;
}

export const AcceptInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [step, setStep] = useState<
    'loading' | 'signup' | 'signin' | 'processing' | 'success' | 'error'
  >('loading');

  const fetchInvitationInfo = useCallback(async () => {
    try {
      const response = await api.get(`/company-users/invitation/${token}`);

      if (response.data.success) {
        const invitationData = response.data.data;
        setInvitation(invitationData);
        setEmail(invitationData.inviteEmail);

        // Firebase AuthにtenantIdを設定
        if (invitationData.companyTenantId) {
          auth.tenantId = invitationData.companyTenantId;
        }

        if (!invitationData.canAccept) {
          if (invitationData.isExpired) {
            setError('この招待は有効期限が切れています');
          } else if (invitationData.isUsed) {
            setError('この招待は既に使用済みです');
          } else {
            setError('この招待は受諾できません');
          }
          setStep('error');
        } else {
          setStep('signup');
        }
      } else {
        setError('招待情報の取得に失敗しました');
        setStep('error');
      }
    } catch (err) {
      setError('招待情報の取得中にエラーが発生しました');
      setStep('error');
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('招待トークンが見つかりません');
      setStep('error');
      return;
    }

    // 招待情報を取得
    fetchInvitationInfo();

    // Firebase認証状態の監視
    const unsubscribe = onAuthStateChanged(auth, () => {
      // 認証状態が変わった時の処理（必要に応じて追加）
    });

    return () => unsubscribe();
  }, [token, fetchInvitationInfo]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    setStep('processing');
    setError(null);

    try {
      // Firebase認証でアカウント作成
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // バックエンドで招待を受諾
      await acceptInvitation(firebaseUser.uid);
    } catch (err: any) {
      console.error('Sign up error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('このメールアドレスは既に使用されています。ログインしてください。');
        setStep('signin');
      } else {
        setError(`アカウント作成に失敗しました: ${err.message}`);
        setStep('signup');
      }
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    setStep('processing');
    setError(null);

    try {
      // Firebase認証でログイン
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // バックエンドで招待を受諾
      await acceptInvitation(firebaseUser.uid);
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(`ログインに失敗しました: ${err.message}`);
      setStep('signin');
    }
  };

  const acceptInvitation = async (firebaseUid: string) => {
    try {
      const response = await api.post('/company-users/accept-invitation', {
        inviteToken: token,
        firebaseUid,
        displayName,
      });

      if (response.data.success) {
        setStep('success');
        // 3秒後に受諾したユーザーの企業ダッシュボードにリダイレクト
        setTimeout(() => {
          // API応答から企業IDを取得してリダイレクト
          const companyId = response.data.data?.companyId || 'admin';
          navigate(`/admin/${companyId}`);
        }, 3000);
      } else {
        setError(`招待の受諾に失敗しました: ${response.data.error}`);
        setStep('error');
      }
    } catch (err: any) {
      setError(`招待の受諾中にエラーが発生しました: ${err.message}`);
      setStep('error');
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">招待情報を確認しています...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">招待エラー</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              ダッシュボードへ戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">招待受諾完了</h2>
            <p className="text-gray-600 mb-2">
              <strong>{invitation?.companyName}</strong>への招待を受諾しました。
            </p>
            <p className="text-sm text-gray-500 mb-6">
              3秒後にダッシュボードにリダイレクトします...
            </p>
            <button
              onClick={() => {
                const companyId = invitation?.companyId;
                navigate(`/admin/${companyId}`);
              }}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            >
              今すぐダッシュボードへ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            TalentScanへの招待
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            <strong>{invitation?.companyName}</strong>からの招待です
          </p>
        </div>

        <div className="bg-white shadow-lg rounded-lg p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {step === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">表示名</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="田中 太郎"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">パスワード</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="6文字以上"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">パスワード確認</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="パスワードを再入力"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                アカウントを作成
              </button>

              <p className="text-center text-sm text-gray-600">
                既にアカウントをお持ちですか？{' '}
                <button
                  type="button"
                  onClick={() => setStep('signin')}
                  className="text-blue-600 hover:text-blue-500"
                >
                  ログイン
                </button>
              </p>
            </form>
          )}

          {step === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">パスワード</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                ログイン
              </button>

              <p className="text-center text-sm text-gray-600">
                新規アカウントを作成しますか？{' '}
                <button
                  type="button"
                  onClick={() => setStep('signup')}
                  className="text-blue-600 hover:text-blue-500"
                >
                  アカウント作成
                </button>
              </p>
            </form>
          )}

          {step === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">処理中...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
